import React, { useState, useEffect, useRef } from 'react';
import AvatarLightbox from './AvatarLightbox';
import ClientProfile from './ClientProfile';
import api from '../../services/api';
import './ContactPanel.css';

function ContactPanel({ contact, onClose, onContactUpdated }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(contact?.name || '');
  const [notes, setNotes] = useState(contact?.notes || '');
  const [saving, setSaving] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    setName(contact?.name || '');
    setNotes(contact?.notes || '');
    setEditing(false);
  }, [contact?.id]);

  useEffect(() => {
    if (editing && nameRef.current) {
      nameRef.current.focus();
      nameRef.current.select();
    }
  }, [editing]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.renameContact(contact.id, name.trim());
      setEditing(false);
      onContactUpdated?.({ ...contact, name: name.trim(), notes: notes.trim() });
    } catch (err) {
      console.error('Erro ao salvar contato:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setName(contact?.name || '');
    setNotes(contact?.notes || '');
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && editing) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape' && editing) {
      e.preventDefault();
      handleCancel();
    }
  };

  if (!contact) return null;

  const initial = (contact.name || '?')[0].toUpperCase();

  return (
    <div className="contact-panel">
      {/* Header */}
      <div className="contact-panel-header">
        <button className="contact-panel-close" onClick={onClose} title="Fechar">
          ✕
        </button>
        <span className="contact-panel-title">Dados do contato</span>
        <button
          className={`contact-panel-edit ${editing ? 'active' : ''}`}
          onClick={() => editing ? handleSave() : setEditing(true)}
          title={editing ? 'Salvar' : 'Editar'}
        >
          {editing ? '✓' : '✎'}
        </button>
      </div>

      {/* Body */}
      <div className="contact-panel-body">
        {/* Avatar + Name + Phone */}
        <div className="contact-panel-profile">
          <AvatarLightbox
            src={contact.avatar_url}
            alt={contact.name}
            className="contact-panel-avatar"
            fallback={
              <span>{initial}</span>
            }
          />

          {editing ? (
            <input
              ref={nameRef}
              className="contact-panel-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nome do contato"
            />
          ) : (
            <h3 className="contact-panel-name">{contact.name}</h3>
          )}

          <p className="contact-panel-phone">{contact.phone}</p>
        </div>

        {/* Notes */}
        <div className="contact-panel-section">
          <label className="contact-panel-label">Notas</label>
          {editing ? (
            <textarea
              className="contact-panel-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione notas sobre seu cliente."
              rows={4}
            />
          ) : (
            <p className="contact-panel-notes">
              {contact.notes || 'Adicione notas sobre seu cliente.'}
            </p>
          )}
        </div>

        {/* Edit actions */}
        {editing && (
          <div className="contact-panel-actions">
            <button className="btn-panel-cancel" onClick={handleCancel}>Cancelar</button>
            <button className="btn-panel-save" onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        )}

        {/* Recado / Status */}
        {contact.status_message && (
          <div className="contact-panel-section">
            <label className="contact-panel-label">Recado</label>
            <p className="contact-panel-status">{contact.status_message}</p>
          </div>
        )}

        {/* Perfil Comportamental — Ficha de Combate */}
        <ClientProfile contactId={contact.id} />
      </div>
    </div>
  );
}

export default ContactPanel;
